
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface DeleteUserDialogProps {
  userToDelete: {
    id: string;
    email: string;
    name: string;
  } | null;
  onClose: () => void;
  onRefresh: () => void;
}

export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  userToDelete,
  onClose,
  onRefresh,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteUser = async () => {
    if (!userToDelete?.id) {
      toast.error("No user ID provided");
      onClose();
      return;
    }

    setIsDeleting(true);
    try {
      // Use the deleteUser action in the edge function
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          action: 'deleteUser',
          userId: userToDelete.id
        }
      });
      
      if (error || (data && data.error)) {
        console.error("Edge function error:", error || data?.error);
        throw new Error(error?.message || data?.error || "Failed to delete user");
      }
      
      toast.success(`User ${userToDelete.name || userToDelete.email} has been deleted`);
      onRefresh();
      onClose();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error(`Failed to delete user: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={!!userToDelete} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete {userToDelete?.name || userToDelete?.email}?
            This action cannot be undone and will permanently delete the user account
            and all associated data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => {
              e.preventDefault();
              handleDeleteUser();
            }}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete User'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
